import React from 'react';
import type { SelectedVase } from '../../../types';
import type { CanvasSectionBlock } from '../sectionRegistry';
import { resolveProductCardSelectionTarget } from '../../../lib/flowBlockResolution';
import { resolveProductCardSelectActionTargets } from '../../../lib/productCardEventBridge';
import { resolveProductSelectionPayload } from '../../../lib/productRecords';

type ProductCardActionsProps = {
  measure?: boolean;
  block: CanvasSectionBlock;
  actions: any;
  template: any;
  buttonJustifyClass: string;
  buttonTypographyStyle: React.CSSProperties;
  primaryButtonVariant: string;
  secondaryButtonVariant: string;
  isSelectedProduct: boolean;
  productCardButtonsDomId?: string;
  shouldRevealButtons: boolean;
  isHoverActive: boolean;
  isAdmin: boolean;
  isInteractPreview: boolean;
  adminPreview?: { onSelectBlock?: (blockId: string | null) => void };
  resolvedProduct: any;
  selectedModelReference: number;
  resolvedName: string;
  resolvedDescription: string;
  resolvedImage: string;
  resolvedImageAlt: string;
  resolvedModelPath: string;
  flow: any;
  sectionId?: string;
  onSelectVase?: (vase: SelectedVase) => void;
  setActiveModelPreview: React.Dispatch<React.SetStateAction<any>>;
};

function primaryButtonStyle({
  template,
  buttonTypographyStyle,
  primaryButtonVariant,
  isSelectedProduct,
}: Pick<
  ProductCardActionsProps,
  'template' | 'buttonTypographyStyle' | 'primaryButtonVariant' | 'isSelectedProduct'
>): React.CSSProperties {
  return {
    ...buttonTypographyStyle,
    borderRadius: `${template.buttonStyle.radius}px`,
    width:
      template.buttonStyle.alignment === 'stretch'
        ? undefined
        : `${template.buttonStyle.primaryWidthPercent}%`,
    maxWidth: '100%',
    backgroundColor:
      primaryButtonVariant === 'outline'
        ? 'transparent'
        : isSelectedProduct
          ? template.buttonStyle.secondaryBackgroundColour
          : template.buttonStyle.primaryBackgroundColour,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor:
      primaryButtonVariant === 'outline'
        ? isSelectedProduct
          ? template.buttonStyle.secondaryBackgroundColour
          : template.buttonStyle.primaryBackgroundColour
        : 'transparent',
    color: isSelectedProduct
      ? template.buttonStyle.secondaryTextColour
      : template.buttonStyle.primaryTextColour,
    flex: template.buttonStyle.alignment === 'stretch' ? '1 1 0%' : undefined,
  };
}

function secondaryButtonStyle({
  template,
  buttonTypographyStyle,
  secondaryButtonVariant,
}: Pick<ProductCardActionsProps, 'template' | 'buttonTypographyStyle' | 'secondaryButtonVariant'>): React.CSSProperties {
  return {
    ...buttonTypographyStyle,
    borderRadius: `${template.buttonStyle.radius}px`,
    width:
      template.buttonStyle.alignment === 'stretch'
        ? undefined
        : `${template.buttonStyle.secondaryWidthPercent}%`,
    maxWidth: '100%',
    backgroundColor:
      secondaryButtonVariant === 'outline' ? 'transparent' : template.buttonStyle.secondaryBackgroundColour,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor:
      secondaryButtonVariant === 'outline' ? template.buttonStyle.secondaryBackgroundColour : 'transparent',
    color: template.buttonStyle.secondaryTextColour,
    flex: template.buttonStyle.alignment === 'stretch' ? '1 1 0%' : undefined,
  };
}

export function ProductCardActions({
  measure = false,
  block,
  actions,
  template,
  buttonJustifyClass,
  buttonTypographyStyle,
  primaryButtonVariant,
  secondaryButtonVariant,
  isSelectedProduct,
  productCardButtonsDomId,
  shouldRevealButtons,
  isHoverActive,
  isAdmin,
  isInteractPreview,
  adminPreview,
  resolvedProduct,
  selectedModelReference,
  resolvedName,
  resolvedDescription,
  resolvedImage,
  resolvedImageAlt,
  resolvedModelPath,
  flow,
  sectionId,
  onSelectVase,
  setActiveModelPreview,
}: ProductCardActionsProps) {
  const commonStyle: React.CSSProperties = {
    gap: `${template.buttonStyle.gap}px`,
    paddingTop: `${template.contentButtonGap}px`,
    opacity: !measure && shouldRevealButtons && !isHoverActive ? 0 : 1,
    transform:
      !measure && shouldRevealButtons && !isHoverActive
        ? `translateY(${template.hoverRevealDistance}px)`
        : 'translateY(0)',
    transition: 'transform 180ms ease, opacity 180ms ease, filter 180ms ease',
  };
  const firstStyle = primaryButtonStyle({
    template,
    buttonTypographyStyle,
    primaryButtonVariant,
    isSelectedProduct,
  });
  const secondStyle = secondaryButtonStyle({
    template,
    buttonTypographyStyle,
    secondaryButtonVariant,
  });

  if (measure) {
    return (
      <div className={`flex w-full pt-3 ${buttonJustifyClass}`} style={commonStyle}>
        <div className="px-3 py-2 text-sm" style={{ ...firstStyle, textAlign: 'center' }}>
          {isSelectedProduct ? 'Selected' : actions.primaryAction.label || 'Select'}
        </div>
        <div className="px-3 py-2 text-sm" style={{ ...secondStyle, textAlign: 'center' }}>
          {actions.secondaryAction.label || 'View in 3D'}
        </div>
      </div>
    );
  }

  return (
    <div
      data-builder-product-card-buttons-id={productCardButtonsDomId}
      className={`mt-auto flex w-full pt-3 ${buttonJustifyClass}`}
      style={commonStyle}
    >
      <button
        type="button"
        data-builder-allow-interaction
        onPointerDown={(event) => {
          if (!isAdmin || isInteractPreview) return;
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (isAdmin && !isInteractPreview) {
            event.preventDefault();
            event.stopPropagation();
            adminPreview?.onSelectBlock?.(block.id);
            return;
          }
          if (actions.primaryAction.action !== 'selectVase' || !resolvedProduct) return;
          const selectedVase = resolveProductSelectionPayload({
            ...resolvedProduct,
            id: selectedModelReference,
            name: resolvedName,
            title: resolvedName,
            shortDescription: resolvedDescription,
            description: resolvedDescription,
            image: resolvedImage,
            featuredImage: resolvedImage,
            imageAlt: resolvedImageAlt,
            modelPath: resolvedModelPath,
          });
          if (!selectedVase) return;
          const target = resolveProductCardSelectionTarget(flow.flows, {
            block,
            sectionId,
            vase: selectedVase,
          });
          if (target) {
            flow.patchFlowValues(target.flowId, target.values);
          }
          const actionTargets = resolveProductCardSelectActionTargets(flow.flows, {
            blockId: block.id,
            blockType: 'product-card',
            sectionId,
          });
          if (actionTargets?.selectFieldId) {
            flow.patchFlowValues(actionTargets.flowId, {
              [actionTargets.selectFieldId]: Date.now(),
            });
          }
          actionTargets?.targetNodeIds.forEach((nodeId: string) => {
            flow.invokeFlowNode(actionTargets.flowId, nodeId);
          });
          onSelectVase?.(selectedVase);
        }}
        className="px-3 py-2 text-sm transition hover:brightness-95"
        style={firstStyle}
      >
        {isSelectedProduct ? 'Selected' : actions.primaryAction.label || 'Select'}
      </button>
      <button
        type="button"
        data-builder-allow-interaction
        disabled={!resolvedModelPath}
        onPointerDown={(event) => {
          if (!isAdmin || isInteractPreview) return;
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (isAdmin && !isInteractPreview) {
            event.preventDefault();
            event.stopPropagation();
            adminPreview?.onSelectBlock?.(block.id);
            return;
          }
          if (!resolvedModelPath || actions.secondaryAction.action !== 'viewModel') return;
          setActiveModelPreview({
            modelPath: resolvedModelPath,
            vaseId:
              Number.isFinite(resolvedProduct?.modelId) && Number(resolvedProduct.modelId) > 0
                ? Number(resolvedProduct.modelId)
                : selectedModelReference,
            title: resolvedName,
          });
        }}
        className="px-3 py-2 text-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-40"
        style={secondStyle}
      >
        {actions.secondaryAction.label || 'View in 3D'}
      </button>
    </div>
  );
}
